import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef } from "react";
import NETS from "vanta/dist/vanta.net.min";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styled from "styled-components";
import GlobalStyles from "./styles/global";
import { Main } from "./styles/styles";

import Landing from "./pages/Landing";
import Founder from "./pages/Founder";
import Register from "./pages/Register";
import Login from "./pages/Login";
import { NEW_HOME } from "./siteConfig";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import VerticalPage from "./components/vertical/VerticalPage";
import { verticals } from "./pages/verticalContent";

// The two pages with the 3D brain load their code (three.js and the brain data) only when visited,
// so the other pages stay light.
const FutureLanding = lazy(() => import("./pages/FutureLanding"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorks"));

// Scroll to the top on page change, or to the #section named in the URL.
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  const lastPath = useRef(pathname);
  useLayoutEffect(() => {
    const changedPage = lastPath.current !== pathname;
    lastPath.current = pathname;
    const timers = [];
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      // Arriving from another page: wait for the new page to lay out, then jump.
      // Same page: glide.
      let tries = 0;
      const seek = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: changedPage ? "instant" : "smooth" });
        else if (tries++ < 20) timers.push(setTimeout(seek, 50));
      };
      timers.push(setTimeout(seek, changedPage ? 120 : 0));
    }
    // Re-measure scroll animations for a newly rendered page. Skipped for same-page
    // jumps, because a refresh would cancel the smooth scroll in progress.
    if (changedPage) {
      timers.push(
        setTimeout(() => {
          ScrollTrigger.refresh();
          if (hash) {
            const el = document.getElementById(hash.slice(1));
            if (el) el.scrollIntoView({ behavior: "instant" });
          }
        }, 400)
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  // Keep scroll-driven animations in sync as fonts, images and videos change the page height.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    let id = 0;
    let lastHeight = document.body.scrollHeight;
    const ro = new ResizeObserver(() => {
      const h = document.body.scrollHeight;
      if (Math.abs(h - lastHeight) < 2) return;
      lastHeight = h;
      clearTimeout(id);
      id = setTimeout(() => ScrollTrigger.refresh(), 250);
    });
    ro.observe(document.body);
    return () => {
      clearTimeout(id);
      ro.disconnect();
    };
  }, []);
  return null;
};

// Vanta lives on its own fixed, click-through layer behind the page. Attaching it to the
// scrolling page made a huge canvas that sat on top of any page rendered after navigation.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const Content = styled(Main)`
  position: relative;
  z-index: 1;
  background-color: transparent;
`;

const App = () => {
  const myRef = useRef(null);
  useEffect(() => {
    const effect = NETS({
      el: myRef.current,
      mouseControls: false,
      touchControls: false,
      gyroControls: false,
      minHeight: 100.0,
      minWidth: 100.0,
      scale: 1.0,
      scaleMobile: 1.0,
      color: 0xc9b9a3,
      backgroundColor: 0xf4efe6,
      points: 8.0,
      maxDistance: 15.0,
      spacing: 20.0,
      showDots: false,
    });
    return () => effect.destroy();
  }, []);
  return (
    <>
      <GlobalStyles />
      <Backdrop ref={myRef} aria-hidden="true" />
      <Content>
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <ScrollManager />
          <NavBar />
          <Suspense fallback={<div style={{ minHeight: "100svh", background: "#121a16" }} />}>
          <Routes>
            <Route path="/" element={NEW_HOME ? <FutureLanding draft /> : <Landing />} />
            <Route path="/classic" element={<Landing />} />
            {/* The draft went live on 30 Sep 2026; /draft now points to the home page. */}
            <Route path="/draft" element={<Navigate to="/" replace />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            {verticals.map((v) => (
              <Route key={v.slug} path={`/${v.slug}`} element={<VerticalPage key={v.slug} data={v} />} />
            ))}
            <Route path="/founder" element={<Founder />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/future" element={NEW_HOME ? <Navigate to="/" replace /> : <FutureLanding />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Footer />
        </BrowserRouter>
      </Content>
    </>
  );
};

export default App;
